import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SignInService } from '../../../../common/services/sign-in/sign-in.service';
import { LottoUser } from '../../../../components/views/sign-in/user.model';
import { Page } from '../../../../common/paging-dto';
import { map } from 'rxjs/operators';
import { DuplicateChecksMapper } from './duplicate-checks.mapper';
import {
  ActionChildEntriesRequest,
  ActionChildEntriesResponse,
  DuplicateCheck,
  DuplicateCheckFilters,
  DuplicateChecksQueryParams,
  DuplicateCheckUnlock,
} from './duplicate-checks.model';
import { DuplicateCheckDTO } from './duplicate-checks.dto';
import { PlayerWsService } from '../../../../common/services/ws/player-service/player-ws.service';

@Injectable({
  providedIn: 'root',
})
export class DuplicateChecksService {
  private http = inject(HttpClient);
  private mapper = inject(DuplicateChecksMapper);
  private signInService = inject(SignInService);
  private playerWsService = inject(PlayerWsService);
  private currentUser: LottoUser = this.signInService.getCurrentUser();

  public isLockedByMe(check: DuplicateCheck) {
    if (!check.lockedBy) {
      return false;
    }
    return check.lockedBy.email === this.currentUser.email;
  }

  public getCurrentUser(): LottoUser {
    return this.currentUser;
  }

  public lockDuplicateCheck(checkId: number): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/duplicate-checks/pending/${checkId}/lock`, {});
  }

  public unlockDuplicateCheck(checkId: number): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/duplicate-checks/pending/${checkId}/unlock`, {});
  }

  public actionChildEntries(request: ActionChildEntriesRequest): Observable<ActionChildEntriesResponse> {
    const backendRequest = {
      checkId: request.checkId,
      childActions: request.childActions.map((action) => ({
        ...action,
        outcome: this.mapper.toBackendOutcome(action.outcome),
      })),
    };
    return this.http.post<ActionChildEntriesResponse>(
      `/player/api/backoffice/duplicate-checks/actions`,
      backendRequest
    );
  }

  public getPendingReviewDuplicateChecks(
    query: DuplicateChecksQueryParams,
    filters: DuplicateCheckFilters
  ): Observable<Page<DuplicateCheck>> {
    let params = this.getParams(query, filters);
    return this.http
      .get<Page<DuplicateCheckDTO>>(`/player/api/backoffice/duplicate-checks/pending`, { params })
      .pipe(map((p) => this.mapper.toModelPage(p)));
  }

  public getArchivedDuplicateChecks(
    query: DuplicateChecksQueryParams,
    filters: DuplicateCheckFilters
  ): Observable<Page<DuplicateCheck>> {
    let params = this.getParams(query, filters);
    return this.http
      .get<Page<DuplicateCheckDTO>>(`/player/api/backoffice/duplicate-checks/archived`, { params })
      .pipe(map((p) => this.mapper.toModelPage(p)));
  }

  private getParams(query: DuplicateChecksQueryParams, filters: DuplicateCheckFilters) {
    let params = new HttpParams()
      .set('pageNumber', (query.pageNumber - 1).toString())
      .set('pageSize', query.pageSize.toString());
    if (filters) {
      if (filters.playerId != null) {
        params = params.set('playerId', String(filters.playerId));
      }
      if (filters.email) {
        params = params.set('email', filters.email);
      }
      if (filters.agentEmail) {
        params = params.set('agentEmail', filters.agentEmail);
      }
      if (filters.lastName) {
        params = params.set('lastName', filters.lastName);
      }
      if (filters.outcome) {
        params = params.set('outcome', filters.outcome);
      }
    }
    if (query) {
      if (query.sortField) {
        params = params.set('sortField', query.sortField);
      }
      if (query.sortDirection) {
        params = params.set('sortDirection', query.sortDirection.toUpperCase());
      }
    }
    return params;
  }

  public duplicateCheckCreatedObservable(): Observable<DuplicateCheck> {
    return this.playerWsService.duplicateCheckCreatedObservable().pipe(map((dto) => this.mapper.toModelCheck(dto)));
  }

  public duplicateCheckLockedObservable(): Observable<any> {
    return this.playerWsService.duplicateCheckLockedObservable();
  }

  public duplicateCheckUnlockedObservable(): Observable<DuplicateCheckUnlock> {
    return this.playerWsService.duplicateCheckUnlockedObservable();
  }

  public duplicateCheckArchivedObservable(): Observable<DuplicateCheck> {
    return this.playerWsService.duplicateCheckArchivedObservable();
  }
}
